import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AuthPage from "@/pages/AuthPage";
import OnboardingPage from "@/pages/OnboardingPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SillyGoose from "@/pages/SillyGoose";
import ProfilePage from "@/pages/ProfilePage";
import Dashboard from "@/pages/Dashboard";
import AssetDetail from "@/pages/AssetDetail";
import CpiReport from "@/pages/CpiReport";
import GdpReport from "@/pages/GdpReport";
import UnemploymentReport from "@/pages/UnemploymentReport";
import PmiReport from "@/pages/PmiReport";
import Economics from "@/pages/Economics";
import Academy from "@/pages/Academy";
import Blockchains from "@/pages/Blockchains";
import Stablecoins from "@/pages/Stablecoins";
import CexExchanges from "@/pages/CexExchanges";
import DexExchanges from "@/pages/DexExchanges";
import CryptoWallets from "@/pages/CryptoWallets";
import TokenList from "@/pages/TokenList";
import DexScreenerPage from "@/pages/DexScreenerPage";
import DefiLlamaPage from "@/pages/DefiLlamaPage";
import LifeInsurance from "@/pages/LifeInsurance";
import Leverage from "@/pages/Leverage";
import Trust from "@/pages/Trust";
import GoldenSummary from "@/pages/GoldenSummary";
import GoldenGoals from "@/pages/GoldenGoals";
import MiscLinks from "@/pages/MiscLinks";
import CompoundInterest from "@/pages/CompoundInterest";
import Forecasting from "@/pages/Forecasting";
import LiveNews from "@/pages/LiveNews";
import FinancialEducation from "@/pages/FinancialEducation";

import { Toaster } from "@/components/ui/sonner";
import SearchBar from "@/components/nav/SearchBar";
import NavLinks from "@/components/nav/NavLinks";
import UserMenu from "@/components/nav/UserMenu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Recently viewed stored in localStorage (consumed by SearchBar + NavLinks mobile overlay)
export function getRecents() {
  try { return JSON.parse(localStorage.getItem("market_recents") || "[]"); }
  catch { return []; }
}
export function addRecent(item) {
  const recents = getRecents().filter(r => !(r.id === item.id && r.type === item.type));
  recents.unshift(item);
  localStorage.setItem("market_recents", JSON.stringify(recents.slice(0, 12)));
}

// Favorites stored in localStorage
const FAV_KEY = "market_favorites";
export function getFavorites() {
  try { return JSON.parse(localStorage.getItem(FAV_KEY) || "[]"); }
  catch { return []; }
}
export function isFavorite(id, type) {
  return getFavorites().some(f => f.id === id && f.type === type);
}
export function toggleFavorite(item) {
  const favs = getFavorites();
  const idx = favs.findIndex(f => f.id === item.id && f.type === item.type);
  if (idx >= 0) {
    favs.splice(idx, 1);
  } else {
    favs.unshift({ id: item.id, symbol: item.symbol, name: item.name, type: item.type, chain: item.chain });
  }
  localStorage.setItem(FAV_KEY, JSON.stringify(favs));
  window.dispatchEvent(new Event("favorites-changed"));
  return idx < 0; // returns true if added
}

function TopNav({ minimal = false }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="bg-zinc-950/80 backdrop-blur-md border-b border-amber-500/20 sticky top-0 z-50" data-testid="top-nav">
      <div className="flex items-center gap-3 px-3 py-2">
        <button data-testid="nav-home" onClick={() => navigate("/")} className="flex items-center gap-2 flex-shrink-0 group">
          <div className="relative">
            <img src="/logo.jpeg" alt="The Golden Nest" className="w-7 h-7 rounded-full ring-1 ring-amber-500/40 group-hover:ring-amber-400 transition" />
            <div className="absolute inset-0 rounded-full bg-amber-500/0 group-hover:bg-amber-500/15 transition" />
          </div>
          <div className="hidden sm:block">
            <h1 className="font-heading text-sm font-bold text-gradient-gold leading-none">The Golden</h1>
            <p className="text-[8px] text-amber-400/70 tracking-widest uppercase">Nest</p>
          </div>
        </button>

        <div className="flex-1 max-w-md">
          <SearchBar />
        </div>

        <UserMenu />
      </div>

      {/* Hide the full nav-link strip on the Nest — bubbles ARE the navigation there. */}
      {!minimal && <NavLinks menuOpen={menuOpen} setMenuOpen={setMenuOpen} />}

      <div className="h-[2px] bg-gradient-to-r from-transparent via-amber-500/60 to-transparent shimmer" aria-hidden="true" />
    </header>
  );
}



function RequireAuth({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  // null = still checking auth — render nothing briefly
  if (user === null) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="auth-loading">
        <div className="text-xs text-zinc-500">Loading…</div>
      </div>
    );
  }
  // false = not authenticated — bounce to sign-in, preserve intended destination
  if (!user) {
    return <Navigate to="/auth" replace state={{ from: location.pathname + location.search }} />;
  }
  return children;
}


function AppLayout() {
  const location = useLocation();
  const isNest = location.pathname === "/";
  const isAuth = location.pathname === "/auth" || location.pathname === "/reset-password";
  return (
    <div className="flex flex-col h-screen overflow-hidden relative">
      <div
        className="fixed inset-0 -z-20 bg-cover bg-center"
        style={{ backgroundImage: "url(/landing-hero.jpeg)" }}
        aria-hidden="true"
      />
      <div
        className={`fixed inset-0 -z-10 transition-all duration-500 ${
          isAuth ? "bg-black/0 backdrop-blur-0" :
          isNest ? "bg-black/0 backdrop-blur-0" :
          "bg-black/75 backdrop-blur-md"
        }`}
        aria-hidden="true"
      />
      {/* On the Nest, hide the regular nav links — bubbles ARE the navigation.
          We keep a minimal utility bar (logo + search + notifications + profile). */}
      <TopNav minimal={isNest} />
      <main
        className="flex-1 overflow-y-auto"
        style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
        data-testid="main-content"
      >
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected routes — require authentication */}
          <Route path="/" element={<RequireAuth><Dashboard /></RequireAuth>} />
          <Route path="/onboarding" element={<RequireAuth><OnboardingPage /></RequireAuth>} />
          <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
          <Route path="/forecasting" element={<RequireAuth><Forecasting /></RequireAuth>} />
          <Route path="/live-news" element={<RequireAuth><LiveNews /></RequireAuth>} />
          <Route path="/economics" element={<RequireAuth><Economics /></RequireAuth>} />
          <Route path="/tokens" element={<RequireAuth><TokenList /></RequireAuth>} />
          <Route path="/dexscreener" element={<RequireAuth><DexScreenerPage /></RequireAuth>} />
          <Route path="/defillama" element={<RequireAuth><DefiLlamaPage /></RequireAuth>} />
          <Route path="/academy" element={<RequireAuth><Academy /></RequireAuth>} />
          <Route path="/financial-education" element={<RequireAuth><FinancialEducation /></RequireAuth>} />
          <Route path="/golden-summary" element={<RequireAuth><GoldenSummary /></RequireAuth>} />
          <Route path="/golden-goals" element={<RequireAuth><GoldenGoals /></RequireAuth>} />
          <Route path="/blockchains" element={<RequireAuth><Blockchains /></RequireAuth>} />
          <Route path="/stablecoins" element={<RequireAuth><Stablecoins /></RequireAuth>} />
          <Route path="/cex" element={<RequireAuth><CexExchanges /></RequireAuth>} />
          <Route path="/dex" element={<RequireAuth><DexExchanges /></RequireAuth>} />
          <Route path="/wallets" element={<RequireAuth><CryptoWallets /></RequireAuth>} />
          <Route path="/misc-links" element={<RequireAuth><MiscLinks /></RequireAuth>} />
          <Route path="/asset/:type/:id" element={<RequireAuth><AssetDetail /></RequireAuth>} />
          <Route path="/silly-goose" element={<RequireAuth><SillyGoose /></RequireAuth>} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <SiteFooter />
      </main>
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-zinc-800/60 bg-zinc-950/80 px-4 sm:px-6 py-4 mt-8" data-testid="site-footer">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-zinc-500">
        <span>
          © {new Date().getFullYear()} The Golden Nest. All rights reserved.
        </span>
        <span className="text-zinc-600 italic">
          Not financial advice. Markets carry risk.
        </span>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppLayout />
      </AuthProvider>
    </BrowserRouter>
  );
}

function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <div className="p-8 max-w-2xl mx-auto text-center" data-testid="not-found-page">
      <div className="border border-amber-500/25 bg-zinc-900/30 rounded-sm p-8">
        <h1 className="font-heading text-3xl text-amber-400 font-bold mb-2">404</h1>
        <p className="text-sm text-zinc-300 mb-1">Page not found</p>
        <p className="text-[11px] text-zinc-500 mb-5">The URL you entered doesn't match any page in The Golden Nest.</p>
        <button
          data-testid="not-found-home-btn"
          onClick={() => navigate("/")}
          className="text-[11px] px-3 py-1.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );
}
